import React, { useEffect, useCallback, useState, useRef } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useOverview } from './hooks';
import { PropertyCard } from './components/PropertyCard';
import { PROPERTY_TYPES, STATUS_OPTIONS, PER_PAGE_OPTIONS } from './constants';
import ResponsiveLayout, { AnchorPosition } from '../common/ResponsiveLayout';
import { useHeader } from '../../contexts/HeaderContext';
import { useTenantPath } from '../../hooks/useTenantPath';
import { useTenant } from '../../contexts/TenantContext';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { SkeletonCard } from './components/SkeletonCard';
import OverviewHeaderActions from './components/OverviewHeaderActions';

// Named exports
export { PropertyCard } from './components/PropertyCard';
export { default as OverviewHeaderActions } from './components/OverviewHeaderActions';
export { useOverview } from './hooks';
export * from './types';
export * from './constants';

// Main Overview component as default export
const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { getPath } = useTenantPath();
  const { isAddEditHost, tenant } = useTenant();
  const {
    listings,
    loading,
    error,
    filters,
    updateFilters,
    refetch,
    currentPage,
    perPage,
    totalCount,
    goToPage,
    changePerPage,
  } = useOverview();
  const { setHeaderData, clearHeaderData } = useHeader();

  // Check if tenant is "isAddEditHost" to show header actions
  const isBrightTenant = !isAddEditHost();
  // Local state for search input (updates immediately for responsive typing)
  const [searchInputValue, setSearchInputValue] = useState(filters.search);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local search input with URL filter changes
  useEffect(() => {
    setSearchInputValue(filters.search);
  }, [filters.search]);

  // Debounced search handler
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Update local state immediately (responsive UI)
    setSearchInputValue(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer to update filters after delay
    debounceTimerRef.current = setTimeout(() => {
      updateFilters({ search: value });
    }, 500); // 500ms debounce delay
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handlePropertyTypeChange = (event: SelectChangeEvent) => {
    updateFilters({ propertyType: event.target.value });
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    updateFilters({ status: event.target.value });
  };

  const handlePerPageChange = (event: SelectChangeEvent) => {
    const newPerPage = parseInt(event.target.value, 10);
    changePerPage(newPerPage);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    goToPage(page);
    // Scroll to top of page when pagination changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddNewListing = useCallback(() => {
    const path = getPath('/create');
    navigate(path);
  }, [getPath, navigate]);

  // Update header data when listings change
  useEffect(() => {
    if (!loading && !error && listings.length >= 0) {
      setHeaderData({
        showAddListingButton: true,
        onAddListing: handleAddNewListing,
        propertiesCount: listings.length,
      });
    }
  }, [listings.length, loading, error, handleAddNewListing, setHeaderData]);

  // Clear header data on unmount only
  useEffect(() => {
    return () => {
      clearHeaderData();
    };
  }, [clearHeaderData]);

  if (error && listings.length === 0) {
    return (
      <ResponsiveLayout>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Listings
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {error}
          </Typography>
          <Button variant="contained" onClick={refetch} sx={{ mt: 2 }}>
            Try Again
          </Button>
        </Box>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout width="100%" anchor={AnchorPosition.TOP}>
      <Container component="div" sx={{ display: 'flex' }} maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            flexDirection: 'column',
            flexGrow: 1,
            width: '100%',
            gap: 3,
            p: 4,
          }}
        >
          {/* Header Actions - Only for bright tenant */}
          {isBrightTenant && <OverviewHeaderActions />}

          {/* Search and Filters */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
            }}
          >
            {/* Search Row */}
            <Box>
              <TextField
                fullWidth
                placeholder="Search by MLS Number..."
                value={searchInputValue}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Filters Row */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <Select
                  value={
                    filters.propertyType === '' ? 'all' : filters.propertyType
                  }
                  onChange={handlePropertyTypeChange}
                  IconComponent={() => <ExpandMore />}
                >
                  {PROPERTY_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ flex: 1, minWidth: 200 }}>
                <Select
                  value={filters.status === '' ? 'all' : filters.status}
                  IconComponent={() => <ExpandMore />}
                  onChange={handleStatusChange}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={perPage.toString()}
                  label="Per Page"
                  onChange={handlePerPageChange}
                >
                  {PER_PAGE_OPTIONS.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value.toString()}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Property Cards Grid */}
          {listings.length === 0 && !loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                flexGrow: 1,
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No properties found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or add a new listing
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              {/* Loading indicator at top - shows when updating existing results */}
              {loading && listings.length >= 0 && (
                <Grid container spacing={3}>
                  <Grid
                    sx={{ display: 'flex', justifyContent: 'center' }}
                    size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                  >
                    <SkeletonCard />
                  </Grid>
                </Grid>
              )}
              {!loading && listings.length > 0 && (
                <Grid container spacing={3}>
                  {listings.map((listing) => {
                    // Handle both old Listing type and new ListingData type
                    const key =
                      'ListingKey' in listing
                        ? listing.ListingKey
                        : listing.Property.LISTING_KEY;
                    return (
                      <Grid
                        sx={{ display: 'flex', justifyContent: 'center' }}
                        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                        key={key}
                      >
                        <PropertyCard
                          listing={listing}
                          isBrightTenant={isBrightTenant}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}

          {/* Pagination Controls */}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              mt: 4,
              pb: 2,
            }}
          >
            {/* Pagination component */}
            <Pagination
              count={Math.ceil((totalCount ?? 0) / perPage)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              disabled={loading}
            />
          </Box>
        </Box>
      </Container>
    </ResponsiveLayout>
  );
};

export default Overview;
